"use client";

import Script from "next/script";
import { useConsent } from "./consent-store";

export interface CustomScriptItem {
  id: string;
  placement: string; // head | body_start | body_end
  consentCategory: string; // essential | analytics | marketing | preferences
  src: string | null;
  code: string | null;
}

/**
 * Renders the admin-managed custom integration scripts (SiteScript rows).
 * Essential scripts load immediately; analytics/marketing/preferences scripts
 * are injected only after the visitor grants the matching cookie consent.
 *
 * `placement` maps to the next/script strategy: body_end → lazyOnload,
 * head/body_start → afterInteractive.
 */
export function CustomScripts({ scripts }: { scripts: CustomScriptItem[] }) {
  const { preferences, hasDecided, hydrated } = useConsent();
  if (!hydrated || scripts.length === 0) return null;

  const allowed = scripts.filter((s) => {
    if (s.consentCategory === "essential") return true;
    if (!hasDecided) return false;
    return Boolean(preferences[s.consentCategory as "analytics" | "marketing" | "preferences"]);
  });
  if (allowed.length === 0) return null;

  return (
    <>
      {allowed.map((s) => {
        const strategy = s.placement === "body_end" ? "lazyOnload" : "afterInteractive";
        if (s.src) {
          return <Script key={s.id} id={`sc-${s.id}`} src={s.src} strategy={strategy} />;
        }
        if (s.code) {
          return (
            <Script key={s.id} id={`sc-${s.id}`} strategy={strategy}>
              {s.code}
            </Script>
          );
        }
        return null;
      })}
    </>
  );
}
